import { useState } from "react";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import { Download, Expand, Upload } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DataTable } from "~/components/assistant/messages/DataTable";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { FullscreenArtifactModal } from "~/components/assistant/messages/FullscreenArtifactModal";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { ExportDialog } from "~/components/assistant/messages/export/ExportDialog";
import { Dataframe } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface DataframeMessageProps {
  dataframe: Dataframe;
  debugMode?: boolean;
}

type TabType = "table" | "code" | "lineage" | "details";

interface Tab {
  id: TabType;
  label: string;
}

// DataframeMessage component with tabbed interface
export function DataframeMessage({ dataframe, debugMode = false }: DataframeMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("table");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const { conversationId, contextId, sendPrompt } = useAssistantContext();
  const user = useUser();
  const exportToWorkbenchEnabled = useFliptBoolean("ai-export-artifact-to-rwb", false, user?.email);

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: dataframe.artifact_id || undefined,
      artifact_type: "dataframe",
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "table", label: "Table" }];
  if (dataframe?.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (dataframe?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  if (debugMode) {
    tabs.push({ id: "details", label: "Details" });
  }

  const renderTabButtons = () =>
    tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors select-none cursor-pointer ${
          activeTab === tab.id
            ? "border-gray-900 text-gray-900"
            : "border-transparent text-gray-600 hover:text-gray-900"
        }`}
      >
        {tab.label}
      </button>
    ));

  const renderTableContent = () => {
    if (activeTab !== "table") return null;
    return (
      <div>
        <DataTable
          data={dataframe.content.data}
          totalDataRows={dataframe.content.totalRows}
          displayedRows={dataframe.content.displayedRows}
        />
        {dataframe?.summary && (
          <div className="mt-3 text-xs text-gray-600">{dataframe.summary}</div>
        )}
      </div>
    );
  };

  const renderCodeContent = () => {
    if (activeTab !== "code" || !dataframe?.code) return null;
    return (
      <>
        {/* Code Header with Copy Button */}
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
          <CopyButton text={dataframe.code} variant="light" size="sm" />
        </div>
        <div className="h-full overflow-hidden rounded-sm">
          {!isLoading && (
            <Highlight
              theme={themes.dracula}
              code={dataframe.code}
              language="python"
              prism={PrismInstance as any}
            >
              {({ style, tokens, getLineProps, getTokenProps, className }) => (
                <pre
                  className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                    className || ""
                  }`}
                  style={style}
                >
                  {tokens.map((line, i) => (
                    <div
                      {...getLineProps({ line, key: i })}
                      key={`line-${i}`}
                      style={{ minHeight: "1.5em" }}
                    >
                      {line.map((token, key) => (
                        <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          )}
          {isLoading && (
            <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
              Loading syntax highlighting...
            </div>
          )}
        </div>
      </>
    );
  };

  const renderLineageContent = () => {
    if (activeTab !== "lineage" || !dataframe?.artifact_id) return null;
    return <LineageTab artifactId={dataframe.artifact_id} />;
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full">
        {/* Tabs */}
        <div className="flex items-center border-b border-gray-200 mb-4">
          {renderTabButtons()}
          {sendPrompt && dataframe.artifact_name && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    sendPrompt(
                      `Create a CSV file from the dataframe artifact "${dataframe.artifact_name}"`
                    );
                    trackEvent("analysis_agent_artifact_download_requested", {
                      dataset_id: contextId || "Unknown",
                      conversation_id: conversationId || undefined,
                      artifact_id: dataframe.artifact_id || undefined,
                      artifact_type: "dataframe",
                    });
                  }}
                  className="ml-auto p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  aria-label="Generate file from artifact"
                >
                  <Download className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Generate file from artifact</TooltipContent>
            </Tooltip>
          )}
          {exportToWorkbenchEnabled && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportDialog(true);
                    trackEvent("analysis_agent_artifact_export_opened", {
                      dataset_id: contextId || "Unknown",
                      conversation_id: conversationId || undefined,
                      artifact_id: dataframe.artifact_id || undefined,
                      artifact_type: "dataframe",
                    });
                  }}
                  className={cn(
                    "p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer",
                    !sendPrompt || !dataframe.artifact_name ? "ml-auto" : "ml-1"
                  )}
                  aria-label="Export to workbench"
                >
                  <Upload className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Export to workbench</TooltipContent>
            </Tooltip>
          )}
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  setIsFullscreen(true);
                  trackEvent("analysis_agent_artifact_fullscreen_opened", {
                    dataset_id: contextId || "Unknown",
                    conversation_id: conversationId || undefined,
                    artifact_id: dataframe.artifact_id || undefined,
                    artifact_type: "dataframe",
                  });
                }}
                className={cn(
                  "p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer",
                  exportToWorkbenchEnabled || (sendPrompt && dataframe.artifact_name)
                    ? "ml-1"
                    : "ml-auto"
                )}
                aria-label="View in full screen"
              >
                <Expand className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>View in full screen</TooltipContent>
          </Tooltip>
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {renderTableContent()}
          {renderCodeContent()}
          {renderLineageContent()}
          {activeTab === "details" && debugMode && (
            <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
              <DetailField label="Dataframe Data" content={dataframe} />
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <FullscreenArtifactModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title="Data Table"
      >
        {/* Tabs (no fullscreen button in modal) */}
        <div className="flex border-b border-gray-200 mb-4 shrink-0">{renderTabButtons()}</div>

        {/* Content fills remaining space */}
        <div className="flex-1 min-h-0 overflow-auto">
          {renderTableContent()}
          {renderCodeContent()}
          {renderLineageContent()}
          {activeTab === "details" && debugMode && (
            <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
              <DetailField label="Dataframe Data" content={dataframe} />
            </div>
          )}
        </div>
      </FullscreenArtifactModal>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        artifact={dataframe}
        artifactType="dataframe"
        conversationId={conversationId || ""}
      />
    </>
  );
}
